import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { API_URL } from '../../../redux/actions/api';
import { updateAppointment } from '../../../redux/actions/dataActions';
import { getMeDetails } from '../../../redux/actions/authActions'; // adjust the path if needed
import { LoadingButton } from '@mui/lab';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import './Calendar.css';
/* ★ סגנון הטיימר (`hold_timer`) חי ב-Form.css. הייבוא מפורש ולא נשען על כך
   שרכיב אחר ייבא אותו קודם — הסתמכות כזו נשברת ברגע שהטופס נטען עצלנית. */
import '../../form/Form.css';
import CustomCalendar from './CustomCalendar';
import StepIndicator from '../../form/StepIndicator';
import SelectionPath from '../../form/SelectionPath';
import { useT } from '../../../i18n';
import { useBackIconProps } from '../../../i18n/DirectionalIcons';
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

/* ★★ F1 — ‏`dayjs.locale('he')` הוסר מכאן. הוא היה קריאה **ברמת-המודול**,
   כלומר כל טעינה של הקובץ הזה נעלה את dayjs כולו על עברית — גם אחרי שהאזרח
   בחר רוסית. הקביעה עברה למקום אחד, `i18n/index.js`, שמחליף אותה בכל
   החלפת-שפה. */

function Calendar(props) {
  const dispatch = useDispatch();
  const t = useT();
  const backIconProps = useBackIconProps();
  const [highlightedDays, setHighlightedDays] = useState([]);
  const { loading } = useSelector((state) => state.async);
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  /* ★★ שם המחלקה מגיע עם התור (`department_title`); הנפילה-לאחור לרשימת-
     ההזמנה נשמרת לבקנד ישן. ⚠️ במסך-העריכה זה קריטי במיוחד: אזרח שעורך
     תור במחלקה שנסגרה ראה כותרת בלי שם-מחלקה בכלל. */
  const department = props.visitor?.department_title
    ? { title: props.visitor.department_title }
    : user?.departments?.find((d) => d.id === props.visitor?.department_id);

  const [currentDate, setCurrentDate] = useState(dayjs());
  const [times, setTimes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [currentSlot, setCurrentSlot] = useState(null);

  // State for error handling with Snackbar
  const [errorMessage, setErrorMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  /* ★ החומרה הייתה קבועה 'error'. ההודעה "זהו התור הקיים שלך" אינה תקלה,
     ובאדום היא נקראת ככישלון — האזרח היה חושב שהעריכה לא נשמרה. */
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  // Scroll-fade logic — identical to Step4
  const timeRef = useRef(null);
  const [showFade, setShowFade] = useState(false);
  const checkFade = () => {
    const el = timeRef.current;
    if (el) setShowFade(el.scrollHeight - el.scrollTop - el.clientHeight > 6);
  };
  useEffect(() => {
    const el = timeRef.current;
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
    const raf = requestAnimationFrame(checkFade);
    return () => cancelAnimationFrame(raf);
  }, [times]);
  // Re-evaluate the fade/chevron when the visible height changes — rotation, and the
  // mobile URL-bar showing/hiding shrinks/grows the locked time window (which changes
  // whether the list overflows). Without this the chevron hint goes stale.
  useEffect(() => {
    const onResize = () => checkFade();
    window.addEventListener('resize', onResize);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', onResize);
    };
  }, []);
  const scrollTimesDown = () => {
    const el = timeRef.current;
    if (!el) return;
    // advance by exactly 2 rows, ALIGNED to row tops, so the next hours never appear
    // cut off at the top. Loop back to the top once the end is reached.
    const slots = el.querySelectorAll('.time-slot');
    const stride = slots.length > 4 ? slots[4].offsetTop - slots[0].offsetTop : 52;
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (maxScroll - el.scrollTop < 6) { el.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const curRow = Math.round(el.scrollTop / stride);
    el.scrollTo({ top: Math.min((curRow + 2) * stride, maxScroll), behavior: 'smooth' });
  };

  // Load slots from the API and set initial date, highlighted days, and times.
  useEffect(() => {
    axios
      .get(API_URL + 'doctors/?schedule_id=' + props.visitor.schedule_id)
      .then((res) => {
        const slt = res?.data?.data?.rows[0]?.slots.sort((a, b) => {
          const dateA = dayjs(a.wdate);
          const dateB = dayjs(b.wdate);
          const timeA = dayjs(a.start_time, 'HH:mm:ss');
          const timeB = dayjs(b.start_time, 'HH:mm:ss');
          if (!dateA.isSame(dateB)) {
            return dateA - dateB;
          }
          return timeA - timeB;
        });
        // EDIT MODE: the citizen's current slot is "taken" by this very appointment, so the
        // availability API never returns it → without injecting it, the current time wouldn't
        // show in the list or be pre-selected. Add a synthetic slot so the current time appears
        // (highlighted blue) and the user sees their existing time before optionally picking another.
        let currentEditSlot = null;
        if (props.visitor.appointed_time) {
          const apptDay  = dayjs(props.visitor.appointed_time).format('YYYY-MM-DD');
          const apptHHmm = dayjs(props.visitor.appointed_time).format('HH:mm');
          currentEditSlot = slt.find(s =>
            s.wdate === apptDay &&
            dayjs('2000-01-01 ' + s.start_time).format('HH:mm') === apptHHmm
          ) || null;
          if (!currentEditSlot) {
            currentEditSlot = {
              id: props.visitor.slot_data_id != null ? props.visitor.slot_data_id : ('current-' + props.visitor.id),
              wdate: apptDay,
              start_time: dayjs(props.visitor.appointed_time).format('HH:mm:ss'),
              is_available: true,
              __current: true,
            };
            slt.push(currentEditSlot);
            slt.sort((a, b) => {
              const dateA = dayjs(a.wdate), dateB = dayjs(b.wdate);
              if (!dateA.isSame(dateB)) { return dateA - dateB; }
              return dayjs(a.start_time, 'HH:mm:ss') - dayjs(b.start_time, 'HH:mm:ss');
            });
          }
        }
        setSlots(slt);

        let initialValue;
        if (props.visitor.appointed_time) {
          // For editing, use the visitor’s appointed time.
          initialValue = dayjs(props.visitor.appointed_time);
        } else {
          // For new appointments, use the first available slot (if any)
          const firstAvailableSlot = slt.find(slot => slot.is_available);
          initialValue = firstAvailableSlot ? dayjs(firstAvailableSlot.wdate) : dayjs();
        }
        setCurrentDate(initialValue);

        // For highlighting, in edit mode highlight all working days in the month.
        const workingDays = slt
          .filter(s => dayjs(s.wdate).isSame(initialValue, 'month'))
          .map(s => Number(dayjs(s.wdate).format('DD')));
        setHighlightedDays([...new Set(workingDays)]);

        // Immediately compute timeslots for the current date.
        const computedTimes = slt.filter(s =>
          dayjs(s.wdate).isSame(initialValue, 'day')
        );
        setTimes(computedTimes);

        // Pre-select the current appointment's time (blue) so the user sees what they have now.
        setCurrentSlot(currentEditSlot);

        // (Optional) Debug logs:
        console.log("Initial currentDate:", initialValue.format('YYYY-MM-DD'));
        console.log("Initial times computed:", computedTimes);
      })
      .catch((err) => {
        console.error("Error fetching doctor slots", err);
      });
  }, [props.visitor.schedule_id, props.visitor.appointed_time]);

  // Whenever currentDate or slots change, recalc times (to be safe).
  useEffect(() => {
    if (slots.length > 0 && currentDate) {
      const newTimes = slots.filter(s =>
        dayjs(s.wdate).isSame(currentDate, 'day')
      );
      setTimes(newTimes);
    }
  }, [currentDate, slots]);

  // The custom day renderer.
  const ServerDay = (props) => {
    const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;
    const isSelected = !outsideCurrentMonth && highlightedDays.indexOf(day.date()) >= 0;
    return (
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        disabled={!isSelected}
        day={day}
      />
    );
  };

  // This function filters times based on the given date.
  const Change = (value) => {
    const newTimes = slots.filter(s =>
      dayjs(s.wdate).isSame(value, 'day')
    );
    setTimes(newTimes);
    setCurrentSlot(null);   // a new day -> drop the old time pick so "אישור" disables
  };

  /* ═══════════════════════════════════════════════════════════════════
     ★★ C1 — גם **עריכה** תופסת את השעה, לא רק קביעה חדשה.
     ⚠️ עד כה ההחזקה הייתה רק בזרימת-ההזמנה (`Form.js`); מסך-העריכה בחר שעה
        והשאיר אותה חשופה עד לאישור, כלומר בדיוק התרחיש שהפיצ'ר בא למנוע —
        רק שכאן האזרח **כבר יש לו תור** והכישלון מרגיש חמור יותר.

     ★ **פייל-אופן**: חוסמים אך ורק על `reason === 'full'`. פיצ'ר כבוי, 401,
       תקלת-רשת או גוף לא-צפוי = "אין מידע" ⇒ ממשיכים. הנכונות נאכפת בשרת
       ברגע השמירה ע"י `ansClaimSlotSeat` האטומי; ההחזקה היא נוחות בלבד.
     ⚠️ ‏**ה-API עוטף כל תשובה ב-`data`** — `res.data.data.ok`. קריאת
        `res.data.ok` מחזירה undefined ומסמנת כל שעה כתפוסה. */
  const holdTokenRef = useRef(null);
  if (!holdTokenRef.current) {
    holdTokenRef.current = 'e' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
  const [holdLeft, setHoldLeft] = useState(0);
  const [holdEndsAt, setHoldEndsAt] = useState(null);

  const releaseHold = (useBeacon) => {
    setHoldEndsAt(null);
    setHoldLeft(0);
    const url = `${API_URL}visitors/release-hold`;
    const body = new URLSearchParams({ hold_token: holdTokenRef.current });
    try {
      if (useBeacon && navigator.sendBeacon) { navigator.sendBeacon(url, body); }
      else { axios.post(url, body).catch(() => {}); }
    } catch (e) { /* שחרור הוא אופטימיזציה — כישלון בו אינו משנה נכונות */ }
  };

  const ChooseTime = async (time) => {
    setCurrentSlot(time);
    setErrorMessage('');

    /* ★ השעה **הנוכחית של התור** אינה נתפסת: היא כבר תפוסה על-ידיו, וטיימר
       עליה מבטיח לאזרח משהו שממילא אינו יכול להיגנב ממנו.
       (‏`__current` מסומן על הסלוט הסינתטי שמוזרק במצב-עריכה למעלה.) */
    const sameAsNow = time.__current
      || (props.visitor?.appointed_time
          && dayjs(props.visitor.appointed_time).format('YYYY-MM-DD') === time.wdate
          && dayjs(props.visitor.appointed_time).format('HH:mm')
             === dayjs('2000-01-01 ' + time.start_time).format('HH:mm'));
    if (sameAsNow) { releaseHold(); return; }

    try {
      const body = new URLSearchParams({
        slot_data_id: String(time.id),
        appointed_time: `${time.wdate} ${time.start_time}`,
        hold_token: holdTokenRef.current,
      });
      const res = await axios.post(`${API_URL}visitors/hold`, body);
      const d = (res?.data?.data ?? res?.data) || {};
      if (d.ok === true) {
        setHoldEndsAt(Date.now() + Number(d.seconds || 0) * 1000);
        setHoldLeft(Number(d.seconds || 0));
      } else if (d.reason === 'full') {
        setCurrentSlot(null);
        setSnackbarSeverity('error');
        /* ★ ההודעות כאן מתורגמות **ברגע היצירה** ולא נשמרות כמפתח: הן טוסט
           חולף (6 שניות) שנעלם מעצמו, ולכן החלפת-שפה בדיוק בתוכו אינה
           תרחיש אמיתי — בשונה משגיאות-טופס שנשארות על המסך. */
        setErrorMessage(t('editAppointment.slotTaken'));
        setSnackbarOpen(true);
      }
    } catch (e) { /* ראה "פייל-אופן" למעלה — ממשיכים בלי החזקה */ }
  };

  /* ספירה-לאחור. בפקיעה מבטלים את הבחירה (ולא את התור!) ומבקשים לבחור שוב —
     הצגת "אישור" פעיל על שעה שכבר אינה שמורה היא בדיוק ההטעיה שנמנעת כאן. */
  useEffect(() => {
    if (!holdEndsAt) { return; }
    const tick = () => {
      const left = Math.max(0, Math.round((holdEndsAt - Date.now()) / 1000));
      setHoldLeft(left);
      if (left <= 0) {
        setHoldEndsAt(null);
        setCurrentSlot(null);
        setSnackbarSeverity('info');
        setErrorMessage(t('editAppointment.holdExpired'));
        setSnackbarOpen(true);
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [holdEndsAt]);

  /* יציאה מהמסך / סגירת-טאב — לא להשאיר שעה נעולה עד לפקיעה. */
  useEffect(() => {
    const onLeave = () => releaseHold(true);
    window.addEventListener('beforeunload', onLeave);
    return () => { window.removeEventListener('beforeunload', onLeave); releaseHold(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This function is called when the user changes the month in the calendar.
  const handleMonthChange = (date) => {
    setTimes([]);

    // Filter all slots for the new month.
    const monthSlots = slots.filter(s => dayjs(s.wdate).isSame(date, 'month'));

    // Build highlighted days from all working slots (regardless of availability) in that month.
    let daysInMonth = monthSlots.map(s => Number(dayjs(s.wdate).format('DD')));

    // If editing and the appointment is in this month, ensure its day is included.
    if (props.visitor.appointed_time && dayjs(props.visitor.appointed_time).isSame(date, 'month')) {
      const appointedDay = dayjs(props.visitor.appointed_time).date();
      if (!daysInMonth.includes(appointedDay)) {
        daysInMonth.push(appointedDay);
      }
    }
    setHighlightedDays([...new Set(daysInMonth)]);

    // Determine the new selected date for the month.
    if (monthSlots.length > 0) {
      let newDate;
      if (props.visitor.appointed_time && dayjs(props.visitor.appointed_time).isSame(date, 'month')) {
        // If editing and the appointment is in this month, use that date.
        newDate = dayjs(props.visitor.appointed_time);
      } else {
        // For a new appointment, choose the first available slot if possible.
        const availableSlots = monthSlots.filter(s => s.is_available);
        if (availableSlots.length > 0) {
          const firstAvailableDay = Math.min(
            ...availableSlots.map(s => dayjs(s.wdate).date())
          );
          newDate = dayjs(date).date(firstAvailableDay);
        } else {
          // Fallback: use the earliest working day.
          const firstWorkingDay = Math.min(
            ...monthSlots.map(s => dayjs(s.wdate).date())
          );
          newDate = dayjs(date).date(firstWorkingDay);
        }
      }
      setCurrentDate(newDate);
      const newTimes = slots.filter(s => dayjs(s.wdate).isSame(newDate, 'day'));
      setTimes(newTimes);
    } else {
      setCurrentDate(date);
      setTimes([]);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const UpdateFunc = async () => {
    if (!currentSlot) {
      return;
    }
    /* ★★ בחירת **אותה שעה** אינה עריכה. קודם היא נשלחה לשרת ככל שמירה אחרת,
       והאזרח קיבל SMS "נקבע לך תור ל…" על תור שכבר היה לו — קריאה מבלבלת
       שנראית כמו תור נוסף (בקשת-משתמש 22/07/2026).
       ★ נעצר כאן ולא רק בשרת: אין טעם בסיבוב-רשת, והאזרח מקבל תשובה מיידית.
         השומר בשרת נשאר בכל זאת — הוא מכסה גם מסלולים שאינם עוברים כאן. */
    const __curDay  = props.visitor?.appointed_time
      ? dayjs(props.visitor.appointed_time).format('YYYY-MM-DD') : null;
    const __curHHmm = props.visitor?.appointed_time
      ? dayjs(props.visitor.appointed_time).format('HH:mm') : null;
    const __newHHmm = dayjs('2000-01-01 ' + currentSlot.start_time).format('HH:mm');
    if (__curDay && __curDay === currentSlot.wdate && __curHHmm === __newHHmm) {
      setSnackbarSeverity('info');
      setErrorMessage(t('editAppointment.sameAppointment'));
      setSnackbarOpen(true);
      return;
    }
    try {
      await dispatch(
        updateAppointment({
          id: props.visitor.id,
          formData: {
            slot_data_id: currentSlot.id,
            appointed_time: currentSlot.start_time,
            timeData: { wdate: currentSlot.wdate },
            /* ★★ חובה: בלי ה-token ההחזקה שהאזרח עצמו יצר על השעה הזו
               נספרת כתופסת-מקום, והשמירה נדחית ב"אין יותר תורים" — על שעה
               ששמורה בשבילו. השרת גם משחרר אותה מיד אחרי התפיסה. */
            hold_token: holdTokenRef.current,
          },
        })
      ).unwrap();

      await dispatch(getMeDetails());
      props.close();
      navigate('/');
    } catch (error) {
      const errMessage =
        typeof error === 'string'
          ? error
          : error?.message || t('editAppointment.unexpectedError');
      // ★ חובה לאפס: אחרי הודעת-מידע, שגיאה אמיתית הייתה מוצגת בירוק/כחול.
      setSnackbarSeverity('error');
      setErrorMessage(errMessage);
      setSnackbarOpen(true);
    }
  };

  return (
    <div className='form_container edit-form'>
      {/* No <Header/> here — this screen now renders inline inside Content, which
          already provides the header (same as the booking flow). */}
      <Button
        aria-label={t('common.back')}
        onClick={() => (props.close ? props.close() : navigate('/'))}
        className='back_btn'
        size='small'
        {...backIconProps}
      >
        {t('common.back')}
      </Button>
      <StepIndicator currentStep={4} readOnly />
      <div className='edit-body'>
      <div className='headDial'>
        <h3>{t('editAppointment.title')}</h3>
        <SelectionPath departmentId={props.visitor?.department_id}
                       departmentTitle={props.visitor?.department_title}
                       serviceTitle={props?.visitor?.schedule?.title} />
      </div>

      {/* ★ F1 — ‏`locale='he'` הוסר: ה-adapter נשען על ה-locale הגלובלי של
          dayjs, שנקבע ב-`i18n/index.js` לפי השפה הנבחרת. */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CustomCalendar
          value={currentDate}
          onChange={(value) => {
            setCurrentDate(value);
            Change(value);
          }}
          onMonthChange={handleMonthChange}
          views={['day']}
          slots={{ day: ServerDay }}
          slotProps={{ day: { highlightedDays } }}
        />
      </LocalizationProvider>

      <div className='time-section edit-time-section'>
        <div className={'time-warp-wrap' + (showFade ? ' has-more' : '')}>
          <div className='time-warp' ref={timeRef} onScroll={checkFade}>
            {times.map((time) => {
              // a11y (WCAG 2.1.1 / 4.1.2): keyboard-focusable button with selected-state.
              const label = dayjs('2023-12-12 ' + time.start_time).format('HH:mm');
              const isActive = currentSlot?.id === time.id;
              return (
                <span
                  key={time.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isActive}
                  aria-label={t('step4.chooseTimeAria', { time: label })}
                  className={'time-slot ' + (isActive ? 'active' : '')}
                  onClick={() => ChooseTime(time)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ChooseTime(time); } }}
                >
                  {label}
                </span>
              );
            })}
          </div>
          {showFade && (
            <button
              type='button'
              className='time-scroll-btn'
              onClick={scrollTimesDown}
              aria-label={t('step4.scrollMoreTimes')}
            >
              <KeyboardArrowDownRoundedIcon />
            </button>
          )}
        </div>
      </div>
      </div>

      {/* ★ הספירה מוצגת רק כשיש החזקה בפועל — שורה קבועה "השעה שמורה" גם
          כשהפיצ'ר כבוי הייתה הבטחה שאיש אינו מקיים.
          ★ אותן מחלקות בדיוק כמו בזרימת-ההזמנה (`Form.css`): אותו רכיב
            למשתמש, ולכן אותו עיצוב — ולא וריאנט שני שיסטה עם הזמן. */}
      {holdLeft > 0 && (
        <div className='hold_timer' aria-live='polite'>
          <span className='hold_timer_txt'>{t('editAppointment.holdReserved')}</span>
          <span className={'hold_timer_num' + (holdLeft <= 60 ? ' is_low' : '')}>
            {Math.floor(holdLeft / 60)}:{String(holdLeft % 60).padStart(2, '0')}
          </span>
        </div>
      )}

      <LoadingButton
        disabled={!currentSlot}
        loading={loading}
        variant='contained'
        className='next-btn'
        onClick={UpdateFunc}
        endIcon={<CheckIcon />}
      >
        {t('common.confirm')}
      </LoadingButton>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: '220px !important' }}
      >
        <Alert severity={snackbarSeverity}>{errorMessage}</Alert>
      </Snackbar>
    </div>
  );
}

export default Calendar;
